import { Table, Button, Row, Col, Form, Pagination } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import Message from './../components/Message';
import Loader from './../components/Loader';
import { useGetTransfersQuery, useDeleteTransferMutation } from './../slices/transfersApiSlice';
import { toast } from 'react-toastify';
import { useState } from 'react';

const StaffTransfer = () => {
  // States for filtering, pagination, and date range
  const [filter, setFilter] = useState('');
  const [pageNumber, setPageNumber] = useState(1);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Fetch paginated transfer data
  const { data, isLoading, error, refetch } = useGetTransfersQuery(pageNumber);

  // Mutation for deleting transfers
  const [deleteTransfer, { isLoading: loadingDelete }] = useDeleteTransferMutation();

  // Handle transfer deletion
  const deleteHandler = async (id) => {
    if (window.confirm('Are you sure you want to delete this transfer?')) {
      try {
        await deleteTransfer(id).unwrap(); // Calls delete mutation
        toast.success('Transfer deleted successfully!');
        refetch(); // Refresh the list after deletion
      } catch (err) {
        toast.error(err?.data?.message || 'Failed to delete transfer.');
      }
    }
  };

  // Filter transfers data based on product name, note, and date range
  const filterTransfers = () => {
  if (!data?.transfers) return [];

  return data.transfers.filter((transfer) => {
    const transferDate = new Date(transfer?.date || new Date());
    const isWithinDateRange =
      (!startDate || transferDate >= new Date(startDate)) &&
      (!endDate || transferDate <= new Date(endDate));

    const lowerFilter = filter.toLowerCase();

    return (
      isWithinDateRange &&
      (
        transfer?.note?.toLowerCase().includes(lowerFilter) || // Filter by note
        transfer?.customerDetails?.name?.toLowerCase().includes(lowerFilter) || // Filter by customer name
        transfer?.transferItems?.some((item) =>
          item?.name?.toLowerCase().includes(lowerFilter) // Filter by item name
        )
      )
    );
  });
};
  // Calculate total transfers revenue
  const calculateTotalTransfers = () => {
    return filterTransfers().reduce((total, transfer) => {
      const transferTotal = transfer?.transferItems?.reduce(
        (subTotal, item) => subTotal + item.qty * item.price,
        0
      );
      return total + transferTotal;
    }, 0);
  };

  // Handle pagination changes
  const paginationHandler = (page) => {
    setPageNumber(page);
  };
  // Calculate total transfers quantity
  const calculateTotalQty = () => {
    return filterTransfers().reduce((totalQty, transfer) => {
      const supplyQty = transfer.transferItems.reduce(
        (subTotalQty, item) => subTotalQty + item.qty ,
        0
      );
      return totalQty + supplyQty;
    }, 0);
  };


  return (
    <>
      <Row className="align-items-center mb-3">
        <Col>
          <h1>Transfers</h1>
        </Col>
        <Col md={4}>
          <Form.Control
            type="text"
            placeholder="Filter by product name or note"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={3}>
          <Form.Control
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            placeholder="Start Date"
          />
        </Col>
        <Col md={3}>
          <Form.Control
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            placeholder="End Date"
          />
        </Col>
        <Col md={6} className="text-end">
          <h4>TotalQty: D{calculateTotalQty().toFixed(2)}</h4>
          <h4>TotalRev: D{calculateTotalTransfers().toFixed(2)}</h4>
        </Col>
      </Row>

      {loadingDelete && <Loader />}
      {isLoading ? (
        <Loader />
      ) : error ? (
        <Message variant="danger">
          {error?.data?.message || 'An error occurred'}
        </Message>
      ) : filterTransfers().length === 0 ? (
        <Message>No transfers match your criteria.</Message>
      ) : (
        <>
          <Table striped bordered hover responsive className="table-sm">
            <thead>
              <tr>
                <th>DATE</th>
                <th>PRODUCT</th>
                <th>QTY</th>
                <th>TOTAL</th>
                <th>NOTE</th>
                <th>PATIENT</th>
                <th>IMAGE</th>
                <th>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {filterTransfers().map((transfer) =>
                transfer?.transferItems?.map((item) => (
                  <tr key={`${transfer?._id}-${item?.product}`}>
                    <td>{new Date(transfer?.date).toLocaleDateString()}</td>
                    <td>{item?.name}</td>
                    <td>{item?.qty}</td>
                    <td>{(item?.qty * item?.price).toFixed(2)}</td>
                    <td>{transfer?.note || 'N/A'}</td>
                    <td>
                    {transfer.customerDetails.name || 'N/A'}
                   </td>
                    <td>
                      {item?.images ? (
                        <img
                          src={item.images[0]}
                          alt="Thumbnail"
                          className="thumbnail-img"
                          style={{
                            width: '10px',
                            height: '10px',
                            objectFit: 'cover',
                            borderRadius: '5px',
                          }}
                        />
                      ) : (
                        <span>No image</span>
                      )}
                    </td>
                    <td>
                      <Button
                        as={Link}
                        to={`/transfer/${transfer?._id}`}
                        variant="light"
                        className="btn-sm"
                      >
                        Details
                      </Button>
                      <Button
                        style={{ display: 'none' }}
                        as={Link}
                        to={`/transfer/${transfer?._id}/edit`}
                        variant="light"
                        className="btn-sm mx-2"
                      >
                        Edit
                        </Button>
                      <Button
                        style={{ display: 'none' }}
                        variant="danger"
                        className="btn-sm mx-2"
                        onClick={() => deleteHandler(transfer?._id)}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>

          {/* Pagination */}
          <Pagination>
            {[...Array(data.pages).keys()].map((x) => (
              <Pagination.Item
                key={x + 1}
                active={x + 1 === pageNumber}
                onClick={() => paginationHandler(x + 1)}
              >
                {x + 1}
              </Pagination.Item>
            ))}
          </Pagination>
        </>
      )}
    </>
  );
};

export default StaffTransfer;